import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../api/client'
import { useTabs } from '../store/tabs'

interface ActorHit {
  did: string
  handle: string
  displayName?: string
  avatar?: string
}

/**
 * Handle typeahead + full-text post search box. Shared by the desktop
 * TopPanel and the mobile shell's top bar / search sheet.
 */
export function SearchBox({ onNavigated }: { onNavigated?: () => void }) {
  const navigate = useNavigate()
  const addTab = useTabs((s) => s.open)

  const [q, setQ] = useState('')
  const [hits, setHits] = useState<ActorHit[]>([])
  const [searching, setSearching] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // When on, the search box switches from "jump to a handle" to a
  // full-text POST search: submitting navigates to /tabs-search, and the
  // live handle typeahead is suppressed. This searches post TEXT across
  // all cached accounts — distinct from the account-name filter in the
  // Accounts panel's "cached accounts" list.
  const [postSearch, setPostSearch] = useState(false)

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (postSearch) { setHits([]); return }
    if (!q.trim()) { setHits([]); return }
    debounceRef.current = setTimeout(() => {
      setSearching(true)
      void api
        .appView<{ actors?: ActorHit[] }>({
          path: `app.bsky.actor.searchActorsTypeahead?q=${encodeURIComponent(
            q.trim().replace(/^@+/, ''),
          )}&limit=8`,
        })
        .then((r) => setHits(r.actors ?? []))
        .catch(() => setHits([]))
        .finally(() => setSearching(false))
    }, 200)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [q, postSearch])

  function pickActor(a: ActorHit) {
    addTab({ handle: a.handle, did: a.did, displayName: a.displayName ?? null, avatar: a.avatar ?? null })
    setQ('')
    setHits([])
    navigate(`/profile/${encodeURIComponent(a.handle)}`)
    onNavigated?.()
  }

  function onSearchSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!q.trim()) return
    if (postSearch) {
      navigate(`/tabs-search?q=${encodeURIComponent(q.trim())}&scope=cached`)
      onNavigated?.()
      return
    }
    if (hits[0]) pickActor(hits[0])
  }

  return (
    <form
      onSubmit={onSearchSubmit}
      className="relative flex min-w-[140px] flex-1 items-center gap-2 px-3"
      style={{ background: '#0a1426' }}
    >
      <span className="shrink-0 text-[11px] text-ink-500">⌕</span>
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={postSearch ? 'search post text… (press ↵)' : 'search handles…'}
        className="h-full min-w-0 flex-1 bg-transparent text-xs text-white outline-none placeholder:text-ink-500"
      />
      <label
        className="flex shrink-0 cursor-pointer select-none items-center gap-1 whitespace-nowrap border px-2 py-0.5 text-[11px] font-semibold"
        style={{
          borderColor: postSearch ? 'var(--accent-strong)' : 'rgba(255,255,255,0.14)',
          color: postSearch ? 'var(--accent-strong)' : '#9aa4b2',
          background: postSearch ? 'rgba(56,189,248,0.10)' : 'transparent',
        }}
        title="post search: full-text search across the cached POSTS of every account (opens the results page). turn off to jump to a handle instead."
        onClick={(e) => e.stopPropagation()}
      >
        <input
          type="checkbox"
          checked={postSearch}
          onChange={(e) => setPostSearch(e.target.checked)}
          className="accent-[color:var(--accent-strong)]"
        />
        post search
      </label>
      {(hits.length > 0 || searching) && (
        <div className="absolute inset-x-0 top-full z-40 max-h-72 overflow-y-auto border border-white/10 bg-ink-900/95 p-1 shadow-xl">
          {searching && hits.length === 0 && (
            <div className="px-2 py-1 text-[11px] text-ink-400">searching…</div>
          )}
          {hits.map((r) => (
            <button
              type="button"
              key={r.did}
              onClick={() => pickActor(r)}
              className="flex w-full items-center gap-2 p-1.5 text-left text-xs hover:bg-white/10"
            >
              {r.avatar ? (
                <img src={r.avatar} alt="" className="h-6 w-6 object-cover" referrerPolicy="no-referrer" />
              ) : (
                <span className="h-6 w-6 bg-gradient-to-br from-sky-500/40 to-violet-500/40" />
              )}
              <span className="min-w-0 flex-1 truncate">
                <span className="text-white">{r.displayName ?? '@' + r.handle}</span>
                <span className="block truncate text-[10px] text-ink-400">@{r.handle}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </form>
  )
}
